import { bindable, useView } from 'aurelia-templating';
import { autoinject, transient } from 'aurelia-dependency-injection';
import { computedFrom, observable } from 'aurelia-binding';
import { DOM, PLATFORM } from 'aurelia-framework';
import { Reflection } from '../../helpers/reflection';

//Load styles
require("./comp-tooltip.scss");

@autoinject
@transient()
@useView(PLATFORM.moduleName("./comp-tooltip.html"))
export class CompTooltip {

    public tooltipContainerEl: HTMLElement;
    public isOverflowing: boolean = false;
    public hasOverflow: boolean;
    public overflowDirection: string;

    @observable()
    public visibleInUI: boolean;

    @bindable
    public onElement: HTMLElement;

    @bindable
    public position: TooltipPosition = TooltipPosition.Center;

    @bindable
    public showArrow: boolean = false;

    @bindable
    public minWidth: number = 0;

    @bindable
    public maxWidth: number = 300;

    @bindable
    public padding: string = "5px 10px";

    @bindable
    public ignoreOverflowDirections: boolean = false;

    @bindable
    public setDelay: number = 0;

    private _isMouseOnTooltip: boolean = false;
    
    private isDelayTimeoutOver: boolean = false;

    @computedFrom(Reflection.getPropertyPath<CompTooltip>(x => x.onElement), Reflection.getPropertyPath<CompTooltip>(x => x.tooltipContainerEl), 
        Reflection.getPropertyPath<CompTooltip>(x => x.visibleInUI), Reflection.getPropertyPath<CompTooltip>(x => x.isOverflowing),
        Reflection.getPropertyPath<CompTooltip>(x => x.position))
    get tooltipPositionX(): number {
        if (this.onElement && this.tooltipContainerEl && this.visibleInUI && !this.isOverflowing) {
            let tooltipX: number;
            const tooltipLeftPosition = this.onElement.getBoundingClientRect().left;
            const tooltipElWidth = this.onElement.offsetWidth;

            if (this.position == TooltipPosition.TopLeft || this.position == TooltipPosition.BottomLeft) {
                tooltipX = tooltipLeftPosition;
            }
            else if (this.position == TooltipPosition.TopRight || this.position == TooltipPosition.BottomRight) {
                tooltipX = tooltipLeftPosition + (tooltipElWidth - this.maxWidth);
            }
            else if (this.position == TooltipPosition.Left) {
                tooltipX = tooltipLeftPosition + tooltipElWidth;
            }
            else if (this.position == TooltipPosition.Right) {
                tooltipX = tooltipLeftPosition - this.maxWidth;
            }
            else {
                tooltipX = tooltipLeftPosition + (tooltipElWidth / 2) - (this.maxWidth / 2);
            }

            return tooltipX;
        }
    }
    
    @computedFrom(Reflection.getPropertyPath<CompTooltip>(x => x.onElement), Reflection.getPropertyPath<CompTooltip>(x => x.tooltipContainerEl), 
    Reflection.getPropertyPath<CompTooltip>(x => x.visibleInUI), Reflection.getPropertyPath<CompTooltip>(x => x.isOverflowing),
    Reflection.getPropertyPath<CompTooltip>(x => x.position), Reflection.getPropertyPath<CompTooltip>(x => x.tooltipContainerEl.offsetHeight))
    get tooltipPositionY() {
        if (this.onElement && this.tooltipContainerEl && this.visibleInUI) {
            let tooltipY: number;
            const tooltipTopPosition = this.onElement.getBoundingClientRect().top;
            const elementHeight = this.onElement.getBoundingClientRect().height;
            const tooltipWrapHeight = this.tooltipContainerEl.offsetHeight;

            if (this.position == TooltipPosition.TopLeft || this.position == TooltipPosition.TopRight) {
                tooltipY = tooltipTopPosition - tooltipWrapHeight;
            }
            else if (this.position == TooltipPosition.BottomLeft || this.position == TooltipPosition.BottomRight) {
                tooltipY = tooltipTopPosition + elementHeight ;
            }
            else if (this.position == TooltipPosition.Left || this.position == TooltipPosition.Right) {
                tooltipY = tooltipTopPosition;
            }
            else {
                tooltipY = tooltipTopPosition - tooltipWrapHeight;
            }

            return tooltipY;
        }

        //If onElement is not visible yet, display tooltip outside client bounds
        return -1000;
    }

    get isDelayFinished(): boolean {
        return this.isDelayTimeoutOver;
    }

    constructor (private element: Element) { }

    attached () {
        window.addEventListener('mousemove', this.checkElementCursorIntersection);
    }

    detached () {
        window.removeEventListener('mousemove', this.checkElementCursorIntersection);
    }

    checkElementCursorIntersection = (event: MouseEvent) => {
        if (!this.onElement) return;

        if(this._isMouseOnTooltip)
            return;

        if (this.isCursorIntersectingElement(event)) {
            if (!this.isOverflowing) {
                if (this.checkOverflow() && !this.ignoreOverflowDirections) {
                    this.isOverflowing = true;
                }
            }
            
            setTimeout(() => {
                this.isDelayTimeoutOver = true;
            }, this.setDelay);

            this.visibleInUI = true;
            this.element.addEventListener("mouseenter", this.onEnter);
            this.element.addEventListener("mouseleave", this.onLeave);

        } else {
            this.isOverflowing = false;
            this.overflowDirection = null;
            this.visibleInUI = false;
            this.isDelayTimeoutOver = false;
            this.element.removeEventListener("mouseenter", this.onEnter);
            this.element.removeEventListener("mouseleave", this.onLeave);
        }
    }

    isCursorIntersectingElement = (event: MouseEvent): boolean => {
        
        //Calculate bounding box of onElement
        const elementXLeft = this.onElement.getBoundingClientRect().x;
        const elementXRight = this.onElement.getBoundingClientRect().x + this.onElement.getBoundingClientRect().width-1;
        const elementYTop = this.onElement.getBoundingClientRect().y;
        const elementYBottom = this.onElement.getBoundingClientRect().y + this.onElement.getBoundingClientRect().height-1;

        //Check if cursor is intersecting element or not
        return !(event.clientX < elementXLeft || event.clientX > elementXRight ||
            event.clientY < elementYTop || event.clientY > elementYBottom);
    }

    public checkOverflow(): boolean {
        const tooltipWrapPositionLeft = this.tooltipContainerEl.getBoundingClientRect().left;
        const tooltipWrapPositionRight = window.innerWidth - this.tooltipContainerEl.getBoundingClientRect().right;

        if (tooltipWrapPositionLeft < 0) {
            this.overflowDirection = 'overflow-left';
            return true;
        }
        else if (tooltipWrapPositionRight < 0) {
            this.overflowDirection = 'overflow-right';
            return true;
        }
        else {
            this.overflowDirection = "";
            return false;
        }
    }

    public visibleInUIChanged() {
        if (this.visibleInUI) {
            const event: CustomEvent = DOM.createCustomEvent("show", {
                bubbles: true,
                detail: {}
            });
            this.element.dispatchEvent(event);
        }
    }

    onEnter = () => { this._isMouseOnTooltip = true; }
    onLeave = () => { this._isMouseOnTooltip = false; }
}


enum TooltipPosition {
    TopLeft = "top-left",
    TopRight = "top-right",
    BottomLeft = "bottom-left",
    BottomRight = "bottom-right",
    Left = "left",
    Right = "right",
    Center = "center"
}